import React from "react";
import {
  Eye,
  Edit3,
  ShieldCheck,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Phone,
  Mail,
  User,
  Calendar,
  Building2,
} from "lucide-react";
import {
  StaffListItem,
  StaffDepartmentLabels,
  EmploymentTypeLabels,
} from "../../types/staff.types";
import { StaffStatusBadge } from "./StaffStatusBadge";
import defaultAvatar from "../../assets/images/default_avatar.jpg";

interface StaffListingTableProps {
  staffList: StaffListItem[];
  isLoading: boolean;
  currentPage: number;
  pageSize: number;
  totalItemsCount: number;
  totalPages: number;
  readOnly?: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onViewDetails: (staff: StaffListItem) => void;
  onEditDetails?: (staff: StaffListItem) => void;
  onUpdateStatus?: (staff: StaffListItem) => void;
  onUploadDoc?: (staff: StaffListItem) => void;
}

export const StaffListingTable: React.FC<StaffListingTableProps> = ({
  staffList,
  isLoading,
  currentPage,
  pageSize,
  totalItemsCount,
  totalPages,
  readOnly = false,
  onPageChange,
  onPageSizeChange,
  onViewDetails,
  onEditDetails,
  onUpdateStatus,
}) => {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Table Container */}
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-emerald-50/60 border-b border-gray-200 text-[11px] font-bold text-gray-600 uppercase tracking-wider">
              <th className="py-3.5 px-4 w-14 text-center">S.No.</th>
              <th className="py-3.5 px-5 min-w-[220px]">Staff Member</th>
              <th className="py-3.5 px-5 min-w-[180px]">Department & Role</th>
              <th className="py-3.5 px-5 min-w-[150px]">Contact</th>
              <th className="py-3.5 px-4 min-w-[120px]">Type</th>
              <th className="py-3.5 px-4 min-w-[110px] text-center">Status</th>
              <th className="py-3.5 px-5 min-w-[200px] text-center">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 text-sm">
            {isLoading ? (
              <tr>
                <td colSpan={8} className="py-16 text-center text-gray-400">
                  <div className="flex flex-col items-center justify-center gap-2">
                    <div className="w-6 h-6 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
                    <span className="text-xs font-medium">Loading staff members...</span>
                  </div>
                </td>
              </tr>
            ) : staffList.length === 0 ? (
              <tr>
                <td colSpan={8} className="py-16 text-center text-gray-400">
                  <div className="flex flex-col items-center justify-center gap-2">
                    <User className="w-10 h-10 text-gray-300" />
                    <span className="text-sm font-medium text-gray-600">No staff members found</span>
                    <span className="text-xs text-gray-400">Try adjusting your search or filters</span>
                  </div>
                </td>
              </tr>
            ) : (
              staffList.map((staff, index) => {
                const serialNumber = (currentPage - 1) * pageSize + index + 1;
                return (
                  <tr
                    key={staff.id}
                    className="hover:bg-slate-50/70 transition-colors"
                  >
                    {/* S.No */}
                    <td className="py-3.5 px-4 text-xs font-medium text-gray-500 text-center">
                      {serialNumber}
                    </td>

                    {/* Staff Member */}
                    <td className="py-3.5 px-5">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full overflow-hidden border border-gray-200 bg-gray-100 flex-shrink-0">
                          <img
                            src={staff.profilePhoto || defaultAvatar}
                            alt={staff.fullName}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="overflow-hidden">
                          <p className="font-bold text-gray-900 text-sm truncate">
                            {staff.fullName}
                          </p>
                          <p className="text-xs text-gray-500 truncate font-mono">
                            {staff.employeeCode ? `  ${staff.employeeCode}` : ""}
                          </p>
                        </div>
                      </div>
                    </td>

                    {/* Department & Role */}
                    <td className="py-3.5 px-5">
                      <div className="space-y-0.5">
                        <span className="inline-block font-semibold text-gray-900 text-xs">
                          {StaffDepartmentLabels[staff.department as any] || staff.department || "General"}
                        </span>
                        <p className="text-xs text-gray-500">
                          {staff.designation || "Staff Member"}
                        </p>
                      </div>
                    </td>

                    {/* Contact */}
                    <td className="py-3.5 px-5">
                      <div className="flex items-center gap-1.5 text-xs text-gray-800 font-medium">
                        <Phone className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                        <span>{staff.mobileNumber || "N/A"}</span>
                      </div>
                    </td>

                    {/* Employment Type */}
                    <td className="py-3.5 px-4">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                        {EmploymentTypeLabels[staff.employmentType as any] || staff.employmentType || "Permanent"}
                      </span>
                    </td>

                    {/* Status */}
                    <td className="py-3.5 px-4 text-center">
                      <StaffStatusBadge status={staff.employeeStatus} />
                    </td>

                    {/* Action Buttons - Clearly visible and labeled */}
                    <td className="py-3.5 px-5">
                      <div className="flex items-center justify-center gap-2">
                        {/* View Button */}
                        <button
                          type="button"
                          onClick={() => onViewDetails(staff)}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-lg transition-colors shadow-2xs"
                          title="View complete staff profile"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          <span>View</span>
                        </button>

                        {!readOnly && (
                          <>
                            {/* Edit Button */}
                            {onEditDetails && (
                              <button
                                type="button"
                                onClick={() => onEditDetails(staff)}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg transition-colors shadow-2xs"
                                title="Edit staff details"
                              >
                                <Edit3 className="w-3.5 h-3.5" />
                                <span>Edit</span>
                              </button>
                            )}

                            {/* Status Button */}
                            {onUpdateStatus && (
                              <button
                                type="button"
                                onClick={() => onUpdateStatus(staff)}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-lg transition-colors shadow-2xs"
                                title="Change status (Active/Inactive/Resigned/Terminated)"
                              >
                                <ShieldCheck className="w-3.5 h-3.5" />
                                <span>Status</span>
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Footer */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-6 py-3.5 border-t border-gray-200 bg-white">
        <div className="flex items-center gap-2 text-xs text-gray-600">
          <span>Rows per page:</span>
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="px-2 py-1 bg-white border border-gray-200 rounded-lg text-xs font-medium focus:ring-1 focus:ring-emerald-500"
          >
            {[10, 20, 50].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
          <span className="text-gray-400">| Total {totalItemsCount} records</span>
        </div>

        <div className="flex items-center gap-1">
          <span className="text-xs text-gray-500 mr-2">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => onPageChange(1)}
            disabled={currentPage === 1 || isLoading}
            className="p-1 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronsLeft className="w-4 h-4" />
          </button>
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1 || isLoading}
            className="p-1 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="px-2.5 py-1 text-xs font-bold text-emerald-700 bg-emerald-50 rounded-lg border border-emerald-200">
            {currentPage}
          </span>
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage >= totalPages || isLoading}
            className="p-1 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          <button
            onClick={() => onPageChange(totalPages)}
            disabled={currentPage >= totalPages || isLoading}
            className="p-1 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronsRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
